import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CreateDMPSubmissionDTO,
  DmpSubmissionDetailsDTO,
  DmpSubmissionDTO,
} from '../../services/dtos/dmp.dto'
import { DmpService } from '../../services/DmpService'
import { getDmpSubmissions } from '../thunks/DmpThunk'
import { DmpSubmission, PaginatedDmpSubmissionsResponse } from '@/services/dmpSubmissionService'

export interface DmpState {
  list: DmpSubmissionDTO[]
  details: DmpSubmissionDetailsDTO | null
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  loading: boolean
  error: string | null
}

export const initialState: DmpState = {
  list: [],
  details: null,
  page: 1,
  pageSize: 25,
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
}

export const doGetDmpByOrders = createAsyncThunk(
  'dmp/getDmpByOrders',
  async (orderIds: number[]) => {
    const response = await DmpService.getDmpByOrders(orderIds)
    return response
  },
)

export const getDmpSubmissionDetails = createAsyncThunk(
  'dmp/getDmpSubmissionDetails',
  async (id: string) => {
    const response = await DmpService.getDmpSubmissionDetails(id)
    return response
  },
)

export const doCreateDmpSubmission = createAsyncThunk(
  'dmp/createDmpSubmission',
  async (dmpSubmission: CreateDMPSubmissionDTO) => {
    const response = await DmpService.createDmpSubmission(dmpSubmission)
    return response
  },
)

export const dmpSlice = createSlice({
  name: 'dmp',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(doGetDmpByOrders.fulfilled, (state, action) => {
      state.list = action.payload
    })
    builder.addCase(getDmpSubmissionDetails.fulfilled, (state, action) => {
      state.details = action.payload
    })
    builder.addCase(doCreateDmpSubmission.fulfilled, (state, action) => {
      state.list.push({
        id: action.payload.id,
        createdAt: action.payload.createdAt,
        updatedAt: action.payload.updatedAt,
        status: action.payload.status,
        error: action.payload.error,
        medicalOrderId: action.payload.medicalOrderId,
        documents: action.payload.documents.map((doc) => doc.id),
        reports: action.payload.reports.map((report) => report.id),
      })
    })
    builder
      .addCase(getDmpSubmissions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getDmpSubmissions.fulfilled,
        (state, { payload }: PayloadAction<PaginatedDmpSubmissionsResponse>) => {
          state.list = payload.items
          state.page = payload.page
          state.pageSize = payload.pageSize
          state.totalCount = payload.totalCount
          state.totalPages = payload.totalPages
          state.loading = false
        },
      )
      .addCase(getDmpSubmissions.rejected, (state, { error }) => {
        state.loading = false
        state.error = error.message ?? 'Failed to load DMP submissions'
      })
  },
})

export default dmpSlice.reducer
